import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useEffect, useMemo, useState } from "react";

export type WorkerRatingValue = "thumbsUp" | "thumbsDown";

type ReasonButtonColor = "primary" | "error";

const CUSTOM_REASON_LABEL = "Other";

type CustomReason = typeof CUSTOM_REASON_LABEL;

const POSITIVE_REASONS = [
  "Reliable & On Time",
  "Team-Oriented",
  "Professional & Courteous",
  "Excellent Patient Care",
] as const;

const NEGATIVE_REASONS = [
  "Late/Unreliable",
  "Difficulty Working With Staff",
  "Professional Conduct Concerns",
  "Disengaged",
] as const;

export type WorkerRatingReason =
  | typeof POSITIVE_REASONS[number]
  | typeof NEGATIVE_REASONS[number]
  | CustomReason;

const LOCAL_STORAGE_KEY_PREFIX = "shift-board-worker-rating-submitted";

export interface WorkerRatingState {
  hasSubmitted: boolean;
  selectedRating: WorkerRatingValue | undefined;
  selectedReasons: WorkerRatingReason[];
  availableReasons: WorkerRatingReason[];
  reasonButtonColor: ReasonButtonColor;
  selectRating: (rating: WorkerRatingValue) => void;
  toggleReason: (reason: WorkerRatingReason) => void;
  updateCustomReason: (reason: string) => void;
  submit: () => void;
  isSubmitDisabled: boolean;
  isCustomReasonSelected: boolean;
  customReason: string;
}

function getStorageKey(shiftId: string): string {
  return `${LOCAL_STORAGE_KEY_PREFIX}:${shiftId}`;
}

function readHasSubmitted(shiftId: string): boolean {
  try {
    const storedValue = window.localStorage.getItem(getStorageKey(shiftId));
    return storedValue === "true";
  } catch {
    return false;
  }
}

function writeHasSubmitted(shiftId: string): void {
  window.localStorage.setItem(getStorageKey(shiftId), "true");
}

export function hasWorkerRatingBeenSubmitted(shiftId: string): boolean {
  return readHasSubmitted(shiftId);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function useWorkerRatingState(shiftId: string): WorkerRatingState {
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(() => readHasSubmitted(shiftId));
  const [selectedRating, setSelectedRating] = useState<WorkerRatingValue | undefined>(undefined);
  const [selectedReasons, setSelectedReasons] = useState<WorkerRatingReason[]>([]);
  const [customReason, setCustomReason] = useState<string>("");

  useEffect(() => {
    setHasSubmitted(readHasSubmitted(shiftId));
    setSelectedRating(undefined);
    setSelectedReasons([]);
    setCustomReason("");
  }, [shiftId]);

  function selectRating(rating: WorkerRatingValue) {
    setSelectedRating((previousRating) => {
      if (previousRating !== rating) {
        setSelectedReasons([]);
        setCustomReason("");
      }

      return rating;
    });
  }

  function toggleReason(reason: WorkerRatingReason) {
    if (!isDefined(selectedRating)) {
      return;
    }

    const allowedReasons: readonly WorkerRatingReason[] =
      selectedRating === "thumbsDown"
        ? [...NEGATIVE_REASONS, CUSTOM_REASON_LABEL]
        : [...POSITIVE_REASONS, CUSTOM_REASON_LABEL];

    if (!allowedReasons.includes(reason)) {
      return;
    }

    setSelectedReasons((previous) => {
      if (previous.includes(reason)) {
        if (reason === CUSTOM_REASON_LABEL) {
          setCustomReason("");
        }

        return previous.filter((value) => value !== reason);
      }

      return [...previous, reason];
    });
  }

  function updateCustomReason(reason: string) {
    setCustomReason(reason);
  }

  function submit() {
    const trimmedCustomReason = customReason.trim();
    const isCustomReasonSelected = selectedReasons.includes(CUSTOM_REASON_LABEL);

    if (!isDefined(selectedRating) || hasSubmitted) {
      return;
    }

    if (isCustomReasonSelected && trimmedCustomReason.length === 0) {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.SHIFT_BOARD_WORKER_RATING_SUBMITTED, {
      shiftId,
      selectedRating,
      selectedReasons,
      customReason: isCustomReasonSelected ? trimmedCustomReason : undefined,
    });
    writeHasSubmitted(shiftId);
    setHasSubmitted(true);
  }

  const availableReasons = useMemo<WorkerRatingReason[]>(() => {
    if (selectedRating === "thumbsUp") {
      return [...POSITIVE_REASONS, CUSTOM_REASON_LABEL];
    }

    if (selectedRating === "thumbsDown") {
      return [...NEGATIVE_REASONS, CUSTOM_REASON_LABEL];
    }

    return [];
  }, [selectedRating]);

  const reasonButtonColor: ReasonButtonColor =
    selectedRating === "thumbsDown" ? "error" : "primary";

  const isCustomReasonSelected = selectedReasons.includes(CUSTOM_REASON_LABEL);

  const isSubmitDisabled =
    !isDefined(selectedRating) ||
    hasSubmitted ||
    (isCustomReasonSelected && customReason.trim().length === 0);

  return {
    hasSubmitted,
    selectedRating,
    selectedReasons,
    availableReasons,
    reasonButtonColor,
    selectRating,
    toggleReason,
    updateCustomReason,
    submit,
    isSubmitDisabled,
    isCustomReasonSelected,
    customReason,
  };
}
